// being used
import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  Button,
  FormControlLabel,
  Switch,
} from "@mui/material";
import moment from "moment";
import {
  getUniqueMonths,
  getUniqueQuarters,
  getUniqueYears,
} from "@thequantumhub/iam-package";
import {
  calculateWeeklyComplianceData,
  calculateMonthlyComplianceData,
  calculateQuarterlyComplianceData,
  calculateYearlyComplianceData,
} from "./ppeRecordsUtils";
import jsPDF from "jspdf";
import "jspdf-autotable";
import PPEChart from "./ppeChart";

import styles from "../../../styles.module.css";
import clsx from "clsx";
import { Typography } from "@material-ui/core";
import { viewportContext } from "../../../context/ViewportContext";

import { getPPEComplianceRow } from "@thequantumhub/iam-package";

const ComplianceTable = ({
  ppeRecords,
  timeFilter,
  columnView,
  strict,
  handleStrictChange,
  handleColumnViewChange,
  handleTimeFilterChange,
}) => {
  const timeUnits =
    timeFilter === "monthly"
      ? getUniqueMonths(ppeRecords)
      : timeFilter === "quarterly"
      ? getUniqueQuarters(ppeRecords)
      : timeFilter === "yearly"
      ? getUniqueYears(ppeRecords)
      : getUniqueWeeks(ppeRecords);

  const [aggregatedData, setAggregatedData] = useState({});
  const [showGroupBy, setShowGroupBy] = useState(false);
  const { screenType } = useContext(viewportContext);

  const paperRef = useRef(null);

  const handleClickOutside = (event) => {
    if (paperRef.current && !paperRef.current.contains(event.target)) {
      setShowGroupBy(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const determineAggregatedData = (
    columnView,
    ppeRecords,
    timeUnits,
    timeFilter,
    strict
  ) => {
    switch (columnView) {
      case "observation":
        return aggregateDataByQuestions(
          ppeRecords,
          timeUnits,
          timeFilter,
          strict
        );
      case "precaution":
        return aggregateDataByPrecautionType(
          ppeRecords,
          timeUnits,
          timeFilter,
          strict
        );
      case "hcwType":
        return aggregateDataByHCWType(
          ppeRecords,
          timeUnits,
          timeFilter,
          strict
        );
      case "unit":
        return aggregateDataByUnit(ppeRecords, timeUnits, timeFilter, strict);
      case "facility":
        return aggregateDataByFacility(
          ppeRecords,
          timeUnits,
          timeFilter,
          strict
        );
      default:
        return aggregateDataByStepTitle(
          ppeRecords,
          timeUnits,
          timeFilter,
          strict
        );
    }
  };

  useEffect(() => {
    const newData = determineAggregatedData(
      columnView,
      ppeRecords,
      timeUnits,
      timeFilter,
      strict
    );
    setAggregatedData(newData);
  }, [ppeRecords, timeUnits, timeFilter, columnView, strict]);

  const complianceData =
    timeFilter === "monthly"
      ? calculateMonthlyComplianceData(ppeRecords)
      : timeFilter === "quarterly"
      ? calculateQuarterlyComplianceData(ppeRecords)
      : timeFilter === "yearly"
      ? calculateYearlyComplianceData(ppeRecords)
      : calculateWeeklyComplianceData(ppeRecords);

  const exportPDF = () => {
    const doc = new jsPDF();
    const tableColumn = [
      "Observation Point",
      ...timeUnits,
      "Compliance (Total)",
    ];

    const tableRows = [];
    Object.entries(aggregatedData).forEach(([key, dataByTimeUnit]) => {
      const tableRow = getPPEComplianceRow(
        timeUnits,
        strict,
        key,
        dataByTimeUnit
      );
      tableRows.push(tableRow);
    });

    doc.autoTable(
      tableColumn,
      tableRows,
      { startY: 20 },
      { styles: { lineColor: [0, 0, 0], lineWidth: 0.5 } }
    );
    doc.save("compliance_report.pdf");
  };

  const exportCSV = () => {
    let csvContent = "data:text/csv;charset=utf-8,";
    const headers = ["Observation Point", ...timeUnits, "Compliance (Total)"];
    csvContent += headers.join(",") + "\r\n";

    Object.entries(aggregatedData).forEach(([key, dataByTimeUnit]) => {
      const tableRow = getPPEComplianceRow(
        timeUnits,
        strict,
        key,
        dataByTimeUnit
      );
      csvContent += tableRow.join(",") + "\r\n";
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "compliance_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderGroupBy = () => {
    return (
      <>
        <Button
          className={clsx(
            columnView === "moment"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
          onClick={() => handleColumnViewChange("moment")}
        >
          Moment
        </Button>
        <Button
          className={clsx(
            columnView === "observation"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
          onClick={() => handleColumnViewChange("observation")}
        >
          Observation Point
        </Button>
        <Button
          className={clsx(
            columnView === "precaution"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
          onClick={() => handleColumnViewChange("precaution")}
        >
          Precaution Type
        </Button>
        <Button
          className={clsx(
            columnView === "hcwType"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
          onClick={() => handleColumnViewChange("hcwType")}
        >
          HCW Type
        </Button>
        <Button
          className={clsx(
            columnView === "unit"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
          onClick={() => handleColumnViewChange("unit")}
        >
          Unit
        </Button>
        <Button
          className={clsx(
            columnView === "facility"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
          onClick={() => handleColumnViewChange("facility")}
        >
          Facility
        </Button>
        <span className={clsx(styles.marginRight2, styles.marginLeft2)}>
          and
        </span>
        <Button
          className={clsx(
            timeFilter === "weekly"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
          onClick={() => handleTimeFilterChange("weekly")}
        >
          Week
        </Button>
        <Button
          className={clsx(
            timeFilter === "monthly"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
          onClick={() => handleTimeFilterChange("monthly")}
        >
          Month
        </Button>
        <Button
          className={clsx(
            timeFilter === "quarterly"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
          onClick={() => handleTimeFilterChange("quarterly")}
        >
          Quarter
        </Button>
        <Button
          className={clsx(
            timeFilter === "yearly"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
          onClick={() => handleTimeFilterChange("yearly")}
        >
          Year
        </Button>
      </>
    );
  };

  return (
    <>
      <div>
        <Paper className={styles.paperStyles}>
          <div className={styles.chartFilterRow}>
            <div className={styles.graphTitleSection}>
              <Typography variant="h5">Graph</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={strict}
                    onChange={handleStrictChange}
                    name="strictCompliance"
                    color="primary"
                  />
                }
                label="Strict Compliance"
                className={styles.marginLeft5}
              />
            </div>
            <div>
              {screenType.isTab || screenType.isMobile ? (
                <>
                  <Button
                    onClick={() => setShowGroupBy(!showGroupBy)}
                    className={clsx(
                      styles.secondaryButton,
                      styles.complianceTableButtons
                    )}
                  >
                    {screenType.isMobile ? "Group" : "Group By"}
                  </Button>
                  {showGroupBy && (
                    <Paper ref={paperRef} className={styles.groupByPaperStyles}>
                      {renderGroupBy()}
                    </Paper>
                  )}
                </>
              ) : (
                <div className={styles.flexCenter}>
                  <span className={clsx(styles.marginRight5)}>Group By</span>
                  {renderGroupBy()}
                </div>
              )}
            </div>
          </div>

          <PPEChart aggregatedData={aggregatedData} timeUnits={timeUnits} />
        </Paper>
      </div>
      <div>
        <Paper className={styles.paperStyles}>
          <div className={styles.titleButtonRow}>
            <Typography variant="h5">Summary</Typography>
            <div className={styles.exportButtonContainer}>
              <div>
                <Button
                  onClick={exportPDF}
                  className={clsx(
                    styles.secondaryButton,
                    styles.complianceTableButtons
                  )}
                >
                  {screenType.isMobile ? "PDF" : "Export PDF"}
                </Button>
                <Button
                  onClick={exportCSV}
                  className={clsx(
                    styles.secondaryButton,
                    styles.complianceTableButtons
                  )}
                >
                  {screenType.isMobile ? "CSV" : "Export CSV"}
                </Button>
              </div>
            </div>
          </div>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    {columnView === "stepTitle"
                      ? "Step Title"
                      : "Observation Point"}
                  </TableCell>
                  {timeUnits.map((unit) => (
                    <TableCell key={unit}>
                      {timeFilter === "monthly"
                        ? `${unit}`
                        : timeFilter === "quarterly"
                        ? `${unit}`
                        : timeFilter === "yearly"
                        ? `${unit}`
                        : `W${unit}`}
                    </TableCell>
                  ))}
                  <TableCell>Compliance (Total)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(aggregatedData).map(([key, dataByTimeUnit]) => {
                  const totalCompliantAnswers = timeUnits.reduce(
                    (sum, unit) =>
                      sum + (dataByTimeUnit[unit]?.compliantAnswers || 0),
                    0
                  );
                  const totalQuestions = timeUnits.reduce(
                    (sum, unit) =>
                      sum + (dataByTimeUnit[unit]?.totalQuestions || 0),
                    0
                  );
                  const totalPercentage =
                    totalQuestions > 0
                      ? (
                          (totalCompliantAnswers / totalQuestions) *
                          100
                        ).toFixed(2)
                      : "0.00";

                  return (
                    <TableRow key={key}>
                      <TableCell>{key}</TableCell>
                      {timeUnits.map((unit) => {
                        const data = dataByTimeUnit[unit] || {
                          compliantAnswers: 0,
                          totalQuestions: 0,
                          nonCompliantWashRub: 0,
                        };
                        const totalCompliance = strict
                          ? data.compliantAnswers
                          : data.compliantAnswers + data.nonCompliantWashRub;
                        const percentage =
                          data.totalQuestions > 0
                            ? (
                                (totalCompliance / data.totalQuestions) *
                                100
                              ).toFixed(2)
                            : "0.00";
                        return (
                          <TableCell
                            key={unit}
                          >{`${percentage}% (${totalCompliance}/${data.totalQuestions})`}</TableCell>
                        );
                      })}
                      <TableCell>{`${totalPercentage}% (${totalCompliantAnswers}/${totalQuestions})`}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </div>
    </>
  );
};
const getUniqueWeeks = (ppeRecords) => {
  const weekSet = new Set(
    ppeRecords.map((record) => moment(record.createdAt).format("W-YYYY"))
  );
  return Array.from(weekSet).sort((a, b) =>
    moment(a, "W-YYYY").diff(moment(b, "W-YYYY"))
  );
};

const aggregateDataByStepTitle = (
  ppeRecords,
  timeUnits,
  timeFilter,
  strict
) => {
  const dataByStepTitle = {};

  ppeRecords.forEach((record) => {
    const recordTimeUnit = moment(record.createdAt).format(
      timeFilter === "quarterly"
        ? "[Q]Q-YYYY"
        : timeFilter === "monthly"
        ? "MM-YYYY"
        : timeFilter === "yearly"
        ? "YYYY"
        : "W-YYYY"
    );

    if (timeUnits.includes(recordTimeUnit)) {
      record.formQuestions.forEach((section) => {
        const stepTitle = section.stepTitle;

        if (!dataByStepTitle[stepTitle]) {
          dataByStepTitle[stepTitle] = {};
        }

        if (!dataByStepTitle[stepTitle][recordTimeUnit]) {
          dataByStepTitle[stepTitle][recordTimeUnit] = {
            compliantAnswers: 0,
            totalQuestions: 0,
            nonCompliantWashRub: 0,
          };
        }

        section.questions.forEach((question) => {
          const responseValue = question.response.value.toLowerCase();
          const outcomeExists = question.response.outcomeExists;
          let isCompliant = responseValue === "yes";

          if (outcomeExists && strict) {
            const outcomeType = question.response.outcome.type;
            const outcomeValue = parseInt(question.response.outcome.value, 10);
            isCompliant =
              isCompliant &&
              !(
                (outcomeType === "wash" && outcomeValue < 20) ||
                (outcomeType === "rub" && outcomeValue < 15)
              );
          }

          if (responseValue === "no") {
            isCompliant = false;
          }

          dataByStepTitle[stepTitle][recordTimeUnit].totalQuestions += 1;
          if (isCompliant) {
            dataByStepTitle[stepTitle][recordTimeUnit].compliantAnswers += 1;
          } else if (outcomeExists && !strict) {
            dataByStepTitle[stepTitle][recordTimeUnit].nonCompliantWashRub += 1;
          }
        });
      });
    }
  });

  return dataByStepTitle;
};

const aggregateDataByQuestions = (
  ppeRecords,
  timeUnits,
  timeFilter,
  strict
) => {
  const dataByQuestion = {};

  ppeRecords.forEach((record) => {
    const recordTimeUnit = moment(record.createdAt).format(
      timeFilter === "quarterly"
        ? "[Q]Q-YYYY"
        : timeFilter === "monthly"
        ? "MM-YYYY"
        : timeFilter === "yearly"
        ? "YYYY"
        : "W-YYYY"
    );

    if (timeUnits.includes(recordTimeUnit)) {
      record.formQuestions.forEach((section) => {
        section.questions.forEach((question) => {
          const questionKey = question.question;

          if (!dataByQuestion[questionKey]) {
            dataByQuestion[questionKey] = {};
          }

          if (!dataByQuestion[questionKey][recordTimeUnit]) {
            dataByQuestion[questionKey][recordTimeUnit] = {
              compliantAnswers: 0,
              totalQuestions: 0,
              nonCompliantWashRub: 0,
            };
          }

          const responseValue = question.response.value.toLowerCase();
          const outcomeExists = question.response.outcomeExists;
          let isCompliant = responseValue === "yes";

          if (outcomeExists && strict) {
            const outcomeType = question.response.outcome.type;
            const outcomeValue = parseInt(question.response.outcome.value, 10);
            isCompliant =
              isCompliant &&
              !(
                (outcomeType === "wash" && outcomeValue < 20) ||
                (outcomeType === "rub" && outcomeValue < 15)
              );
          }

          if (responseValue === "no") {
            isCompliant = false;
          }

          dataByQuestion[questionKey][recordTimeUnit].totalQuestions += 1;
          if (isCompliant) {
            dataByQuestion[questionKey][recordTimeUnit].compliantAnswers += 1;
          } else if (outcomeExists && !strict) {
            dataByQuestion[questionKey][
              recordTimeUnit
            ].nonCompliantWashRub += 1;
          }
        });
      });
    }
  });

  return dataByQuestion;
};

const aggregateDataByPrecautionType = (
  ppeRecords,
  timeUnits,
  timeFilter,
  strict
) => {
  const dataByPrecautionType = {};

  ppeRecords.forEach((record) => {
    const recordTimeUnit = moment(record.createdAt).format(
      timeFilter === "quarterly"
        ? "[Q]Q-YYYY"
        : timeFilter === "monthly"
        ? "MM-YYYY"
        : timeFilter === "yearly"
        ? "YYYY"
        : "W-YYYY"
    );

    if (timeUnits.includes(recordTimeUnit)) {
      const precautionType = record.precautions;

      if (!dataByPrecautionType[precautionType]) {
        dataByPrecautionType[precautionType] = {};
      }

      if (!dataByPrecautionType[precautionType][recordTimeUnit]) {
        dataByPrecautionType[precautionType][recordTimeUnit] = {
          compliantAnswers: 0,
          totalQuestions: 0,
          nonCompliantWashRub: 0,
        };
      }

      record.formQuestions.forEach((section) => {
        section.questions.forEach((question) => {
          const responseValue = question.response.value.toLowerCase();
          const outcomeExists = question.response.outcomeExists;
          let isCompliant = responseValue === "yes";

          if (outcomeExists && strict) {
            const outcomeType = question.response.outcome.type;
            const outcomeValue = parseInt(question.response.outcome.value, 10);
            isCompliant =
              isCompliant &&
              !(
                (outcomeType === "wash" && outcomeValue < 20) ||
                (outcomeType === "rub" && outcomeValue < 15)
              );
          }

          if (responseValue === "no") {
            isCompliant = false;
          }

          dataByPrecautionType[precautionType][
            recordTimeUnit
          ].totalQuestions += 1;
          if (isCompliant) {
            dataByPrecautionType[precautionType][
              recordTimeUnit
            ].compliantAnswers += 1;
          } else if (outcomeExists && !strict) {
            dataByPrecautionType[precautionType][
              recordTimeUnit
            ].nonCompliantWashRub += 1;
          }
        });
      });
    }
  });

  return dataByPrecautionType;
};

const aggregateDataByHCWType = (ppeRecords, timeUnits, timeFilter, strict) => {
  const dataByHCWType = {};

  ppeRecords.forEach((record) => {
    const recordTimeUnit = moment(record.createdAt).format(
      timeFilter === "quarterly"
        ? "[Q]Q-YYYY"
        : timeFilter === "monthly"
        ? "MM-YYYY"
        : timeFilter === "yearly"
        ? "YYYY"
        : "W-YYYY"
    );

    if (timeUnits.includes(recordTimeUnit)) {
      const hcwType = record.healthCareWorker;

      if (!dataByHCWType[hcwType]) {
        dataByHCWType[hcwType] = {};
      }

      if (!dataByHCWType[hcwType][recordTimeUnit]) {
        dataByHCWType[hcwType][recordTimeUnit] = {
          compliantAnswers: 0,
          totalQuestions: 0,
          nonCompliantWashRub: 0,
        };
      }

      record.formQuestions.forEach((section) => {
        section.questions.forEach((question) => {
          const responseValue = question.response.value.toLowerCase();
          const outcomeExists = question.response.outcomeExists;
          let isCompliant = responseValue === "yes";

          if (outcomeExists && strict) {
            const outcomeType = question.response.outcome.type;
            const outcomeValue = parseInt(question.response.outcome.value, 10);
            isCompliant =
              isCompliant &&
              !(
                (outcomeType === "wash" && outcomeValue < 20) ||
                (outcomeType === "rub" && outcomeValue < 15)
              );
          }

          if (responseValue === "no") {
            isCompliant = false;
          }

          dataByHCWType[hcwType][recordTimeUnit].totalQuestions += 1;
          if (isCompliant) {
            dataByHCWType[hcwType][recordTimeUnit].compliantAnswers += 1;
          } else if (outcomeExists && !strict) {
            dataByHCWType[hcwType][recordTimeUnit].nonCompliantWashRub += 1;
          }
        });
      });
    }
  });

  return dataByHCWType;
};

const aggregateDataByUnit = (ppeRecords, timeUnits, timeFilter, strict) => {
  const dataByUnit = {};

  ppeRecords.forEach((record) => {
    const recordTimeUnit = moment(record.createdAt).format(
      timeFilter === "quarterly"
        ? "[Q]Q-YYYY"
        : timeFilter === "monthly"
        ? "MM-YYYY"
        : timeFilter === "yearly"
        ? "YYYY"
        : "W-YYYY"
    );

    if (timeUnits.includes(recordTimeUnit)) {
      const unit = record.unit;

      if (!dataByUnit[unit]) {
        dataByUnit[unit] = {};
      }

      if (!dataByUnit[unit][recordTimeUnit]) {
        dataByUnit[unit][recordTimeUnit] = {
          compliantAnswers: 0,
          totalQuestions: 0,
          nonCompliantWashRub: 0,
        };
      }

      record.formQuestions.forEach((section) => {
        section.questions.forEach((question) => {
          const responseValue = question.response.value.toLowerCase();
          const outcomeExists = question.response.outcomeExists;
          let isCompliant = responseValue === "yes";

          if (outcomeExists && strict) {
            const outcomeType = question.response.outcome.type;
            const outcomeValue = parseInt(question.response.outcome.value, 10);
            isCompliant =
              isCompliant &&
              !(
                (outcomeType === "wash" && outcomeValue < 20) ||
                (outcomeType === "rub" && outcomeValue < 15)
              );
          }

          if (responseValue === "no") {
            isCompliant = false;
          }

          dataByUnit[unit][recordTimeUnit].totalQuestions += 1;
          if (isCompliant) {
            dataByUnit[unit][recordTimeUnit].compliantAnswers += 1;
          } else if (outcomeExists && !strict) {
            dataByUnit[unit][recordTimeUnit].nonCompliantWashRub += 1;
          }
        });
      });
    }
  });

  return dataByUnit;
};

const aggregateDataByFacility = (ppeRecords, timeUnits, timeFilter, strict) => {
  const dataByFacility = {};

  ppeRecords.forEach((record) => {
    const recordTimeUnit = moment(record.createdAt).format(
      timeFilter === "quarterly"
        ? "[Q]Q-YYYY"
        : timeFilter === "monthly"
        ? "MM-YYYY"
        : timeFilter === "yearly"
        ? "YYYY"
        : "W-YYYY"
    );

    if (timeUnits.includes(recordTimeUnit)) {
      const facility = record.facility;

      if (!dataByFacility[facility]) {
        dataByFacility[facility] = {};
      }

      if (!dataByFacility[facility][recordTimeUnit]) {
        dataByFacility[facility][recordTimeUnit] = {
          compliantAnswers: 0,
          totalQuestions: 0,
          nonCompliantWashRub: 0,
        };
      }

      record.formQuestions.forEach((section) => {
        section.questions.forEach((question) => {
          const responseValue = question.response.value.toLowerCase();
          const outcomeExists = question.response.outcomeExists;
          let isCompliant = responseValue === "yes";

          if (outcomeExists && strict) {
            const outcomeType = question.response.outcome.type;
            const outcomeValue = parseInt(question.response.outcome.value, 10);
            isCompliant =
              isCompliant &&
              !(
                (outcomeType === "wash" && outcomeValue < 20) ||
                (outcomeType === "rub" && outcomeValue < 15)
              );
          }

          if (responseValue === "no") {
            isCompliant = false;
          }

          dataByFacility[facility][recordTimeUnit].totalQuestions += 1;
          if (isCompliant) {
            dataByFacility[facility][recordTimeUnit].compliantAnswers += 1;
          } else if (outcomeExists && !strict) {
            dataByFacility[facility][recordTimeUnit].nonCompliantWashRub += 1;
          }
        });
      });
    }
  });

  return dataByFacility;
};

export default ComplianceTable;
